import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { render } from 'lit';
import { map } from 'lit/directives/map.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { live } from 'lit/directives/live.js';
import { getMessagePlural } from '@/localization.js';
import messages from '../localization.json';
import { EVENTS } from '../constants';
import styles from '../style/select.scss?inline';
import { ATTRIBUTES, DEFAULT_INPUT_PROPERTIES } from '@/components/input/constants.js';
import { FormValidationController } from '@/controllers/FormValidationController.js';
import debounce from 'lodash/debounce';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitSelect extends LitElement {
    static formAssociated = true;

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        ...DEFAULT_INPUT_PROPERTIES,
        _displayValue: { type: String, state: true },
        size: { type: String },
        multiple: { type: Boolean, reflect: true },
        open: { type: Boolean, state: true },
        defaultIcon: { type: String, attribute: 'default-icon' },
        showLeftIcon: { type: Boolean, attribute: 'show-left-icon' },
        showRightIcon: { type: Boolean, attribute: 'show-right-icon' },
        hasLeftIcons: { type: Boolean, state: true },
        hasRightIcons: { type: Boolean, state: true },
        clearable: { type: Boolean },
        searchable: { type: Boolean, reflect: true },
        _searchString: { type: String, state: true },
    };

    constructor() {
        super();

        this.label = null;
        this.placeholder = null;
        this.multiple = false;
        this.open = false;
        this.defaultIcon = '';
        this.form = null;
        this.showLeftIcon = false;
        this.showRightIcon = false;
        this.searchable = false;
        this._searchString = '';

        this._internals = this.attachInternals();
        this._defaultOption = null;
        this._selectedOptions = null;
        this._displayValue = this.placeholder;
        this._inputStyleRef = createRef();
        this._optionsSlotRef = createRef();
        this._displayValueInputRef = createRef();
        this.inputRef = createRef();
        this._searchInputRef = createRef();
        this._movedSlots = false;
        this._defaultValue = null;
        this._isArray = false;

        this._resizeObserver = new ResizeObserver(this._handleResizeObserve);
        this._validationController = new FormValidationController(this, {
            getValue: () => this.value ? this.value.toString() : this.value,
            getInputRef: () => this.inputRef,
        });
    }

    get type() {
        return this.multiple ? 'select-multiple' : 'select-one';
    }

    get value() {
        if (this.multiple) {
            if (this._isArray) {
                return this.selectedOptions.map((item) => item.value);
            }

            return this.selectedOptions.map((item) => item.value).join(', ');
        }

        return this.selectedOptions?.[0]?.value ?? '';
    }

    /**
     * @param {string | string[]} newValue
     */
    set value(newValue) {
        if (!Array.isArray(newValue)) {
            newValue = String(newValue ?? '');
        }

        this._syncOptionsFromValue(newValue);

        if (this.error) {
            this.checkValidity(false);
        }
    }

    get selectedOptions() {
        return (this._selectedOptions ??= this.options?.filter((option) => option.hasAttribute('selected')));
    }

    get options() {
        return (this._options ??= this.getOptionsFromSlot());
    }

    set options(options) {
        this._options = Array.from(options);
    }

    get selectedOption() {
        if (this.multiple) {
            return this.selectedOptions;
        }

        return this.selectedOptions[0] ?? null;
    }

    get selectedIndex() {
        return this.options.findIndex((el) => el.selected);
    }

    focus(options) {
        this.inputRef?.value?.focus(options);
    }

    getOptionsFromSlot() {
        const nodes = this._optionsSlotRef?.value
            ? this._optionsSlotRef.value.assignedNodes({
                  flatten: true,
              })
            : Array.from(this.children);

        return nodes.filter((node) => node.nodeName === 'SOTBIT-SELECT-OPTION');
    }

    getFormValue() {
        const formData = new FormData();

        if (this._isArray) {
            const raw = this.value;
            let values = [];
            if (Array.isArray(raw)) {
                values = raw;
            } else {
                values = [raw];
            }

            values.forEach((v) => {
                formData.append(this.name, v);
            });
        } else {
            formData.append(this.name, this.value);
        }

        return formData;
    }

    disconnectedCallback() {
        this._movedSlots = false;
        super.disconnectedCallback();
        document.removeEventListener(EVENTS.CLICK, this._handleDocumentClick);
    }

    formAssociatedCallback(form) {
        this._validationController.removeInputHandlers();
        this._internals?.setFormValue(this.getFormValue());
        this.form = form;
        this._validationController?.validate();
        this._validationController.setInputHandlers();
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formResetCallback() {
        this.value = this._defaultValue;
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    reportValidity() {
        return this.checkValidity(true);
    }

    willUpdate(changedProperties) {
        if (
            changedProperties.has('open') &&
            typeof changedProperties.get('open') !== 'undefined' &&
            changedProperties.get('open') !== this.open
        ) {
            if (this.open) {
                this.show();
            } else {
                this.hide();
            }
        }

        if (changedProperties.has('name')) {
            this._isArray = this.name?.endsWith('[]');
        }

        if (changedProperties.has('_searchString')) {
            this._filterOptions();
        }
    }

    _handleResizeObserve = () => {
        if (this.open) {
            this.hide();
        }
    };

    _createModal() {
        const modal = document.createElement('sotbit-dropdown-content');

        modal.setDropdown(this);
        modal.toggleAttribute('max-height', this.maxHeight);
        modal.setAttribute('use-teleport', '');
        modal.setAttribute('variant', 'select');

        const inputContainer = this._inputStyleRef?.value?.getInputContainer();

        if (inputContainer) {
            modal?.setRelativeElement(inputContainer);
        }

        return modal;
    }

    show() {
        this._modal = this._createModal();

        document.addEventListener('click', this._handleDocumentClick);

        this._movedSlots = true;

        if (this.searchable) {
            render(this._getSearchInputTemplate(), this._modal);
            this._filterOptions();
        }

        this.options?.forEach((option) => this._modal.appendChild(option));

        const handleClose = () => {
            this.removeAttribute('open');
            if (this.searchable) {
                this._resetSearch();
            }
        };

        this._modal.addEventListener('close', handleClose, { once: true });
        this._modal.addEventListener('remove', handleClose, { once: true });

        document.body.appendChild(this._modal);
        window.addEventListener('resize', this._handleResizeObserve);

        if (this.searchable) {
            requestAnimationFrame(() => {
                const searchInput = this._modal.querySelector('sotbit-input');
                if (searchInput) {
                    searchInput.focus();
                }
            });
        }
    }

    hide() {
        this._movedSlots = true;
        this.open = false;

        if (this.searchable) {
            this._resetSearch();
        }

        if (this._modal) {
            this._modal.remove();
            this._modal = null;
            this.options?.forEach((option) => this.appendChild(option));
        }

        document.removeEventListener('click', this._handleDocumentClick);
        window.removeEventListener('resize', this._handleResizeObserve);
    }

    _handleSearchInput = (e) => {
        this._searchString = e.target.value || '';
    };

    _filterOptions() {
        if (!this.searchable) {
            return;
        }

        const query = this._searchString.toLowerCase().trim();
        this.options?.forEach((option) => {
            const text = option.innerText.toLowerCase().trim();
            const matches = !query || text.includes(query);
            option.toggleAttribute('hidden', !matches);
        });
    }

    _resetSearch() {
        this._searchString = '';
    }

    updated(changedProperties) {
        super.updated(changedProperties);

        if (this.multiple && changedProperties.has('_displayValue')) {
            const fullText = this.selectedOptions.map((item) => item.innerText.trim()).join(', ');

            if (this._displayValue === fullText && this.selectedOptions.length > 0) {
                this._checkDisplayValueOverflow();
            }
        }

        if (changedProperties.has('_displayValue')) {
            this._inputStyleRef.value?.calculatePlaceholderShow();
            this._inputStyleRef.value?.calculateSmallLabel();
        }

        if (changedProperties.has('label') && typeof changedProperties.get('label') !== 'undefined') {
            this._setOptionsSize();
        }
    }

    /**
     * @param {string[] | string} newValue
     * @private
     */
    _syncOptionsFromValue(newValue) {
        if (!this.options || this.options.length === 0) {
            return;
        }

        let value = newValue;
        if (!Array.isArray(value)) {
            value = this.multiple && typeof value === 'string'
                ? value.split(', ').filter((v) => v)
                : [value];
        }

        this.options.forEach((opt) => opt.removeAttribute('selected'));

        this._selectedOptions = this.options.filter((opt) => {
            const shouldBeSelected = value.includes(opt.value);
            opt.toggleAttribute('selected', shouldBeSelected);
            return shouldBeSelected;
        });

        if(!this.selectedOptions.length) {
            if(this._defaultOption === null) {
                this._defaultOption = this.options.find(opt => !opt.value) || null;

                //if not clearable one option always need select, default select work
                if(!this._defaultOption && !this.multiple && !this.clearable) {
                    this._defaultOption = this.options[0] || null;
                }
            }

            this._defaultOption?.setAttribute('selected', '');
            this._selectedOptions = this._defaultOption ? [this._defaultOption] : [];
        }

        this._internals.setFormValue(this.getFormValue());
        this._updateDisplayValue();
        this._syncInput();
    }

    _syncInput() {
        const input = this.inputRef.value;
        if(input) {
            input.value = this.value ? this.value.toString() : this.value;
        }
    }

    _checkDisplayValueOverflow() {
        const input = this._displayValueInputRef.value;

        if (!input) {
            return;
        }

        requestAnimationFrame(() => {
            if (input.scrollWidth > input.clientWidth) {
                this._displayValue = getMessagePlural(messages, 'selectedOptions', this.selectedOptions.length, {
                    '#COUNT#': this.selectedOptions.length,
                });
            }
        });
    }

    _updateDisplayValue() {
        const oldDisplayValue = this._displayValue;
        if (this.multiple) {
            if (this.selectedOptions?.length > 0) {
                this._displayValue = this.selectedOptions.map((item) => item.innerText.trim()).join(', ');
            } else {
                this._displayValue = this.placeholder;
            }
        } else {
            this._displayValue = this.selectedOptions[0]?.innerText.trim() || this.placeholder;
        }

        this.hasLeftIcons = this.getSelectedOptionLeftIcons()?.length > 0;
        this.hasRightIcons = this.getSelectedOptionRightIcons()?.length > 0;
        this._inputStyleRef?.value?.calculateSmallLabel();

        this.requestUpdate('_displayValue', oldDisplayValue);
    }

    handleSlotChange() {
        this._slotChangeDebounce();
    }

    _slotChangeDebounce = debounce(() => {
        if (this._movedSlots) {
            this._movedSlots = false;
            return;
        }

        this.options = this.getOptionsFromSlot();
        this._selectedOptions = this.options.filter(option => option.hasAttribute('selected'));
        this._syncOptionsFromValue(this.value);

        if (this._defaultValue === null) {
            this._defaultValue = this.value ?? '';
        }

        for (const option of this.options) {
            option.toggleAttribute('multiple', this.multiple);
            option.setAttribute('size', this.size);
            option.removeEventListener('click', this._handleSelectOption);
            option.addEventListener('click', this._handleSelectOption);
        }

        this._setOptionsSize();

        this.dispatchEvent(
            new Event(EVENTS.OPTIONS_CHANGE, {
                bubbles: true,
                composed: true,
            })
        );
    }, 60);

    _setOptionsSize() {
        for (const option of this.options) {
            if (!option.hasAttribute('size') && !this.label?.trim()?.length) {
                option.setAttribute('size', 's');
            }
        }
    }

    _handleDocumentClick = (event) => {
        if (this.open && !event.composedPath().includes(this)) {
            this.open = this.multiple && event.composedPath().includes(this._modal);
        }
    };

    toggleDropdown() {
        if (this.disabled) {
            return;
        }

        //TODO: need timeout for wait close virtual keyboard if it was open
        setTimeout(() => {
            this.open = !this.open;
        }, 60);
    }

    resetClickHandler(e) {
        e.preventDefault();
        e.stopPropagation();
        this.selectOption(null, true);
    }

    selectOption(option, dispatchEvent = false) {
        if (this.disabled || option?.hasAttribute('disabled')) {
            return;
        }

        if (this.multiple) {
            const emptyOption = this.options.find(option => !option.value);

            if(!option?.value) {
                this.selectedOptions.forEach(option => {
                    option.removeAttribute('selected');
                })

                this._selectedOptions = option || emptyOption ? [option || emptyOption] : [];
            } else {
                const index = this.selectedOptions.findIndex((item) => item.value === option?.value);

                if (index >= 0) {
                    this._selectedOptions.splice(index, 1);
                    option?.removeAttribute('selected');
                } else if (option) {
                    this._selectedOptions.push(option);
                    option.setAttribute('selected', '');
                }
            }

            if(!this._selectedOptions.length) {
                this._selectedOptions = emptyOption ? [emptyOption] : [];
            } else {
                if(option?.value) {
                    const emptyOptionSelectedIndex = this.selectedOptions.findIndex((item) => !item.value);
                    if(emptyOptionSelectedIndex >= 0) {
                        this.selectedOptions[emptyOptionSelectedIndex]?.removeAttribute('selected');
                        this._selectedOptions.splice(emptyOptionSelectedIndex, 1);
                    }
                }

                this._selectedOptions = [...this._selectedOptions];
            }
        } else {
            this.options.forEach((option) => option.removeAttribute('selected'));
            option?.setAttribute('selected', '');
            this._selectedOptions = option ? [option] : [];
        }

        if (this._isArray) {
            this.value = this.selectedOptions.map((item) => item.value);
        } else {
            this.value = this.selectedOptions.map((item) => item.value).join(', ');
        }

        if (dispatchEvent) {
            this.dispatchEvent(
                new CustomEvent(EVENTS.CHANGE, {
                    detail: {
                        value: this.value,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
        }

        this._internals.setFormValue(this.getFormValue());
        this._updateDisplayValue();
    }

    _handleSelectOption = ({ currentTarget }) => {
        this.selectOption(currentTarget, true);
    };

    getSelectedOptionLeftIcons() {
        if (this.multiple || !this.selectedOption) {
            return [];
        }

        if (typeof this.selectedOption.getLeftIcons === 'undefined') {
            return [];
        }

        return this.selectedOption?.getLeftIcons();
    }

    getSelectedOptionRightIcons() {
        if (this.multiple || !this.selectedOption) {
            return [];
        }

        if (typeof this.selectedOption.getRightIcons === 'undefined') {
            return [];
        }

        return this.selectedOption?.getRightIcons();
    }

    _getSearchInputTemplate() {
        return html`
            <div class="select-search-container" slot="header">
                <sotbit-input size="s" .value=${live(this._searchString)} @input=${this._handleSearchInput}>
                    <sotbit-icon inert variant="secondary" icon="search" size="s" slot="icon-left"></sotbit-icon>
                </sotbit-input>
            </div>
        `;
    }

    render() {
        const cloneAndConvertToHtml = (node) => {
            const cloneNode = node.cloneNode(true);
            return html`${unsafeHTML(cloneNode.outerHTML)}`;
        };

        return html`
            <input
                class="hidden-input"
                type="text"
                ${ref(this.inputRef)}
                .value="${this.value?.toString()}"
                ?required="${this.required}"
                ?disabled="${this.disabled}"
                ?readonly="${this.readonly}"
            />
            <sotbit-input-style
                size="${this.size || nothing}"
                label="${this.label || nothing}"
                @click=${this.toggleDropdown}
                default-slot
                default-slot-position="top"
                ${ref(this._inputStyleRef)}
                class="input"
                ?error="${this.error}"
                error-text="${this.errorText || nothing}"
                helper-text="${this.helperText || nothing}"
                ?clearable="${this.clearable && this.selectedOptions.length}"
                no-readonly-style
                exportparts="clear-button-wrapper, clear-button, input-container"
            >
                <input
                    slot="input"
                    type="text"
                    readonly
                    value="${this._displayValue}"
                    ${ref(this._displayValueInputRef)}
                    ?required="${this.required}"
                    ?disabled="${this.disabled}"
                />
                ${this.showLeftIcon
                    ? this.hasLeftIcons
                        ? map(this.getSelectedOptionLeftIcons(), cloneAndConvertToHtml)
                        : html`<sotbit-icon size="l" no-padding icon="select" slot="icon-left"></sotbit-icon>`
                    : ''}
                ${this.showRightIcon
                    ? this.hasRightIcons > 0
                        ? map(this.getSelectedOptionRightIcons(), cloneAndConvertToHtml)
                        : html`<sotbit-icon size="l" no-padding icon="select" slot="icon-right"></sotbit-icon>`
                    : ''}

                <sotbit-icon slot="icon-right" size="m" icon="dropdown"></sotbit-icon>
                <div hidden>
                    <slot ${ref(this._optionsSlotRef)} @slotchange=${this.handleSlotChange}></slot>
                </div>
                <slot name="clear-button" slot="clear-button" @click="${this.resetClickHandler}">
                    <sotbit-icon icon="close" size="m" variant="secondary" pointer></sotbit-icon>
                </slot>
            </sotbit-input-style>
        `;
    }
}
